import { initAxios } from '../../../utils/axiosHelper';
import {
  IAuditLogActor,
  IAuditLogDatabaseFilters,
  IAuditLogPageResponse,
  IAuditLogWithItems,
} from './audit.d';

const toAuditReadError = (err: unknown): unknown => {
  const status = (err as any)?.response?.status;
  if (status === 401) {
    return err;
  }
  const serverMessage = (err as any)?.response?.data?.message;
  const detail = serverMessage || (err as any)?.message || 'unknown error';
  return new Error(
    status
      ? `Audit read failed (${status}): ${detail}`
      : `Audit read failed: ${detail}`,
  );
};

export const getAuditLogsByDatabaseId = async (
  databaseId: string,
  limit = 50,
  offset = 0,
  includeErrors = false,
): Promise<IAuditLogWithItems[]> => {
  try {
    if (!databaseId) {
      return [];
    }
    const instance = await initAxios();
    if (!instance) {
      return [];
    }

    const resp = await instance.get(
      `/bite-db/audit-logs/database/${databaseId}`,
      {
        params: { limit: limit, offset: offset, includeErrors: includeErrors ? 'true' : undefined },
      },
    );
    return resp.data.data || [];
  } catch (err) {
    console.error('Error fetching audit logs by database ID:', err);
    return [];
  }
};

export const getAuditLogsByDatabaseIdPaginated = async (
  databaseId: string,
  limit = 10,
  offset = 0,
  includeErrors = false,
  filters?: IAuditLogDatabaseFilters,
): Promise<IAuditLogPageResponse> => {
  const safeLimit = limit > 0 ? limit : 10;
  const safeOffset = offset >= 0 ? offset : 0;
  const defaultResponse: IAuditLogPageResponse = {
    data: [],
    pagination: {
      totalItems: 0,
      totalPages: 1,
      currentPage: Math.floor(safeOffset / safeLimit) + 1,
      limit: safeLimit,
      offset: safeOffset,
    },
  };

  try {
    if (!databaseId) {
      return defaultResponse;
    }
    const instance = await initAxios();
    if (!instance) {
      return defaultResponse;
    }

    const resp = await instance.get(
      `/bite-db/audit-logs/database/${databaseId}`,
      {
        params: {
          limit: safeLimit,
          offset: safeOffset,
          includeErrors: includeErrors ? 'true' : undefined,
          startDate: filters?.startDate || undefined,
          endDate: filters?.endDate || undefined,
          actions:
            filters?.actions && filters.actions.length > 0
              ? filters.actions.join(',')
              : undefined,
          search: filters?.search || undefined,
        },
      },
    );

    const pagination = resp.data.pagination || defaultResponse.pagination;
    return {
      data: resp.data.data || [],
      pagination: {
        totalItems: Number(pagination.totalItems || 0),
        totalPages: Number(pagination.totalPages || 1),
        currentPage: Number(
          pagination.currentPage || Math.floor(safeOffset / safeLimit) + 1,
        ),
        limit: Number(pagination.limit || safeLimit),
        offset: Number(pagination.offset || safeOffset),
      },
    };
  } catch (err) {
    console.error('Error fetching paginated audit logs by database ID:', err);
    return defaultResponse;
  }
};

// Distinct action types recorded for a database -- populates the Audit Log
// filter modal's action-type dropdown.
export const getAuditLogActionsByDatabaseId = async (
  databaseId: string,
  selectedTeamId: string,
): Promise<string[]> => {
  try {
    if (!databaseId || !selectedTeamId) {
      return [];
    }
    const instance = await initAxios();
    if (!instance) {
      return [];
    }

    const resp = await instance.get(
      `/bite-db/audit-logs/database/${databaseId}/actions`,
      {
        headers: { 'x-selected-team-id': selectedTeamId },
      },
    );
    return resp.data.data || [];
  } catch (err) {
    console.error('Error fetching audit log actions by database ID:', err);
    return [];
  }
};

export const getAuditLogsByGroupIdPaginated = async (
  groupId: string,
  limit = 10,
  offset = 0,
  includeErrors = false,
  databaseType?: string,
): Promise<IAuditLogPageResponse> => {
  const safeLimit = limit > 0 ? limit : 10;
  const safeOffset = offset >= 0 ? offset : 0;
  const defaultResponse: IAuditLogPageResponse = {
    data: [],
    pagination: {
      totalItems: 0,
      totalPages: 1,
      currentPage: Math.floor(safeOffset / safeLimit) + 1,
      limit: safeLimit,
      offset: safeOffset,
    },
  };

  try {
    if (!groupId) {
      return defaultResponse;
    }
    const instance = await initAxios();
    if (!instance) {
      return defaultResponse;
    }

    const resp = await instance.get(`/bite-db/audit-logs/group/${groupId}`, {
      params: {
        limit: safeLimit,
        offset: safeOffset,
        includeErrors: includeErrors ? 'true' : undefined,
        databaseType: databaseType,
      },
    });

    const pagination = resp.data.pagination || defaultResponse.pagination;
    return {
      data: resp.data.data || [],
      pagination: {
        totalItems: Number(pagination.totalItems || 0),
        totalPages: Number(pagination.totalPages || 1),
        currentPage: Number(
          pagination.currentPage || Math.floor(safeOffset / safeLimit) + 1,
        ),
        limit: Number(pagination.limit || safeLimit),
        offset: Number(pagination.offset || safeOffset),
      },
    };
  } catch (err) {
    console.error('Error fetching paginated audit logs by group ID:', err);
    return defaultResponse;
  }
};

export const getAuditLogsByEntityId = async (
  entityId: string,
  limit = 50,
  offset = 0,
): Promise<IAuditLogWithItems[]> => {
  try {
    if (!entityId) {
      console.error('getAuditLogsByEntityId: No entityId provided');
      return [];
    }

    const instance = await initAxios();
    if (!instance) {
      console.error('getAuditLogsByEntityId: Failed to initialize axios');
      return [];
    }

    const resp = await instance.get(`/bite-db/audit-logs/entity/${entityId}`, {
      params: { limit: limit, offset: offset },
    });
    return resp.data.data || [];
  } catch (err) {
    console.error('Error fetching audit logs by entity ID:', err);
    return [];
  }
};

export const getAuditLogsByEntityIdPaginated = async (
  entityId: string,
  limit = 5,
  offset = 0,
): Promise<IAuditLogPageResponse> => {
  const safeLimit = limit > 0 ? limit : 5;
  const safeOffset = offset >= 0 ? offset : 0;
  const defaultResponse: IAuditLogPageResponse = {
    data: [],
    pagination: {
      totalItems: 0,
      totalPages: 1,
      currentPage: Math.floor(safeOffset / safeLimit) + 1,
      limit: safeLimit,
      offset: safeOffset,
    },
  };

  try {
    if (!entityId) {
      return defaultResponse;
    }

    const instance = await initAxios();
    if (!instance) {
      return defaultResponse;
    }

    const resp = await instance.get(`/bite-db/audit-logs/entity/${entityId}`, {
      params: { limit: safeLimit, offset: safeOffset },
    });

    const pagination = resp.data.pagination || defaultResponse.pagination;
    return {
      data: resp.data.data || [],
      pagination: {
        totalItems: Number(pagination.totalItems || 0),
        totalPages: Number(pagination.totalPages || 1),
        currentPage:
          Number(pagination.currentPage || Math.floor(safeOffset / safeLimit) + 1),
        limit: Number(pagination.limit || safeLimit),
        offset: Number(pagination.offset || safeOffset),
      },
    };
  } catch (err) {
    console.error('Error fetching paginated audit logs by entity ID:', err);
    return defaultResponse;
  }
};

export const getAuditLogsByEntityAssociationPaginated = async (
  entityId: string,
  databaseEntityId: string,
  databaseId: string,
  selectedTeamId: string,
  limit = 5,
  offset = 0,
  filters?: IAuditLogDatabaseFilters,
): Promise<IAuditLogPageResponse> => {
  const safeLimit = limit > 0 ? limit : 5;
  const safeOffset = offset >= 0 ? offset : 0;
  const defaultResponse: IAuditLogPageResponse = {
    data: [],
    pagination: {
      totalItems: 0,
      totalPages: 1,
      currentPage: Math.floor(safeOffset / safeLimit) + 1,
      limit: safeLimit,
      offset: safeOffset,
    },
  };

  if (!entityId || !databaseEntityId || !databaseId || !selectedTeamId) {
    throw new Error(
      'getAuditLogsByEntityAssociationPaginated: entityId, databaseEntityId, databaseId and selectedTeamId are all required',
    );
  }

  const instance = await initAxios();
  if (!instance) {
    throw new Error(
      'getAuditLogsByEntityAssociationPaginated: failed to initialize request',
    );
  }

  const resp = await instance
    .get(
      `/bite-db/audit-logs/database/${databaseId}/entity/${entityId}/association/${databaseEntityId}`,
      {
        params: {
          limit: safeLimit,
          offset: safeOffset,
          startDate: filters?.startDate || undefined,
          endDate: filters?.endDate || undefined,
          actions:
            filters?.actions && filters.actions.length > 0
              ? filters.actions.join(',')
              : undefined,
          userIds:
            filters?.userIds && filters.userIds.length > 0
              ? filters.userIds.join(',')
              : undefined,
          search: filters?.search || undefined,
        },
        headers: { 'x-selected-team-id': selectedTeamId },
      },
    )
    .catch((err: unknown) => {
      throw toAuditReadError(err);
    });

  const pagination = resp.data.pagination || defaultResponse.pagination;
  return {
    data: resp.data.data || [],
    pagination: {
      totalItems: Number(pagination.totalItems || 0),
      totalPages: Number(pagination.totalPages || 1),
      currentPage: Number(
        pagination.currentPage || Math.floor(safeOffset / safeLimit) + 1,
      ),
      limit: Number(pagination.limit || safeLimit),
      offset: Number(pagination.offset || safeOffset),
    },
  };
};

// Distinct action types recorded against this entity association --
// populates the entity detail page's Audit Log filter modal's dropdown.
// Throws (rather than returning []) so a failed lookup isn't mistaken for an
// entity with no recorded actions.
export const getAuditLogActionsByEntityAssociation = async (
  entityId: string,
  databaseEntityId: string,
  databaseId: string,
  selectedTeamId: string,
): Promise<string[]> => {
  const instance = await initAxios();
  if (!instance) {
    throw new Error(
      'getAuditLogActionsByEntityAssociation: failed to initialize request',
    );
  }
  const resp = await instance
    .get(
      `/bite-db/audit-logs/database/${databaseId}/entity/${entityId}/association/${databaseEntityId}/actions`,
      { headers: { 'x-selected-team-id': selectedTeamId } },
    )
    .catch((err: unknown) => {
      throw toAuditReadError(err);
    });
  return resp.data.data || [];
};

export const getAuditLogActorsByEntityAssociation = async (
  entityId: string,
  databaseEntityId: string,
  databaseId: string,
  selectedTeamId: string,
): Promise<IAuditLogActor[]> => {
  const instance = await initAxios();
  if (!instance) {
    throw new Error(
      'getAuditLogActorsByEntityAssociation: failed to initialize request',
    );
  }
  const resp = await instance
    .get(
      `/bite-db/audit-logs/database/${databaseId}/entity/${entityId}/association/${databaseEntityId}/actors`,
      { headers: { 'x-selected-team-id': selectedTeamId } },
    )
    .catch((err: unknown) => {
      throw toAuditReadError(err);
    });
  return resp.data.data || [];
};

export const getAuditLogsByTariffId = async (
  tariffId: string,
  limit = 50,
  offset = 0,
): Promise<IAuditLogWithItems[]> => {
  try {
    if (!tariffId) {
      console.error('getAuditLogsByTariffId: No tariffId provided');
      return [];
    }

    const instance = await initAxios();
    if (!instance) {
      console.error('getAuditLogsByTariffId: Failed to initialize axios');
      return [];
    }

    const resp = await instance.get(`/bite-db/audit-logs/tariff/${tariffId}`, {
      params: { limit: limit, offset: offset },
    });
    return resp.data.data || [];
  } catch (err) {
    console.error('Error fetching audit logs by tariff ID:', err);
    return [];
  }
};

export const getAuditLogsByProductId = async (
  productId: string,
  limit = 50,
  offset = 0,
): Promise<IAuditLogWithItems[]> => {
  try {
    if (!productId) {
      console.error('getAuditLogsByProductId: No productId provided');
      return [];
    }

    const instance = await initAxios();
    if (!instance) {
      console.error('getAuditLogsByProductId: Failed to initialize axios');
      return [];
    }

    const resp = await instance.get(
      `/bite-db/audit-logs/product/${productId}`,
      {
        params: { limit: limit, offset: offset },
      },
    );
    return resp.data.data || [];
  } catch (err) {
    console.error('Error fetching audit logs by product ID:', err);
    return [];
  }
};

export const getAuditLogsByProductIdPaginated = async (
  productId: string,
  limit = 5,
  offset = 0,
): Promise<IAuditLogPageResponse> => {
  const safeLimit = limit > 0 ? limit : 5;
  const safeOffset = offset >= 0 ? offset : 0;
  const defaultResponse: IAuditLogPageResponse = {
    data: [],
    pagination: {
      totalItems: 0,
      totalPages: 1,
      currentPage: Math.floor(safeOffset / safeLimit) + 1,
      limit: safeLimit,
      offset: safeOffset,
    },
  };

  try {
    if (!productId) {
      return defaultResponse;
    }

    const instance = await initAxios();
    if (!instance) {
      return defaultResponse;
    }

    const resp = await instance.get(`/bite-db/audit-logs/product/${productId}`, {
      params: { limit: safeLimit, offset: safeOffset },
    });

    const pagination = resp.data.pagination || defaultResponse.pagination;
    return {
      data: resp.data.data || [],
      pagination: {
        totalItems: Number(pagination.totalItems || 0),
        totalPages: Number(pagination.totalPages || 1),
        currentPage:
          Number(pagination.currentPage || Math.floor(safeOffset / safeLimit) + 1),
        limit: Number(pagination.limit || safeLimit),
        offset: Number(pagination.offset || safeOffset),
      },
    };
  } catch (err) {
    console.error('Error fetching paginated audit logs by product ID:', err);
    return defaultResponse;
  }
};
